import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Paper,
  Chip,
  Avatar
} from '@mui/material';
import {
  CloudQueue,
  Security,
  DataUsage,
  Business,
  TrendingUp,
  Support,
  Speed,
  Analytics
} from '@mui/icons-material';

const HomePage = () => {
  const services = [
    {
      icon: <CloudQueue sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Cloud Solutions',
      description: 'Transform your infrastructure with our comprehensive cloud services and migration expertise.',
      features: ['Cloud Migration', 'Hybrid Cloud', 'Cloud Security']
    },
    {
      icon: <Security sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Cyber Resilience',
      description: 'Protect your business with advanced cybersecurity solutions and threat intelligence.',
      features: ['Security Assessment', 'Threat Detection', 'Incident Response']
    },
    {
      icon: <DataUsage sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Data & AI',
      description: 'Unlock the power of your data with AI-driven insights and analytics solutions.',
      features: ['Data Analytics', 'AI Implementation', 'Machine Learning']
    },
    {
      icon: <Business sx={{ fontSize: 40, color: '#1976d2' }} />,
      title: 'Digital Transformation',
      description: 'Modernize your business processes with cutting-edge technology solutions.',
      features: ['Process Automation', 'Digital Strategy', 'Change Management']
    }
  ];

  const insights = [
    {
      title: 'The Future of AI in Business',
      description: 'How artificial intelligence is reshaping enterprise operations and creating new opportunities.',
      author: 'Dr. Sarah Chen',
      role: 'Chief Technology Officer',
      readTime: '5 min read'
    },
    {
      title: 'Cybersecurity Trends 2024',
      description: 'Emerging threats and the strategies organizations need to protect their digital assets.',
      author: 'Michael Rodriguez',
      role: 'Security Expert',
      readTime: '7 min read'
    },
    {
      title: 'Cloud Migration Best Practices',
      description: 'A comprehensive guide to successful cloud transformation and optimization strategies.',
      author: 'Jennifer Liu',
      role: 'Cloud Architect',
      readTime: '6 min read'
    }
  ];

  const stats = [
    { number: '500+', label: 'Projects Completed' },
    { number: '50+', label: 'Expert Consultants' },
    { number: '10+', label: 'Years Experience' },
    { number: '99%', label: 'Client Satisfaction' }
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          py: 12,
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '2.5rem', md: '3.5rem' },
                  fontWeight: 'bold',
                  color: '#1a1a1a',
                  mb: 3,
                  lineHeight: 1.2
                }}
              >
                Transform Your Business with Technology Excellence
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  color: '#666',
                  mb: 4,
                  lineHeight: 1.6
                }}
              >
                We help organizations navigate digital transformation with proven expertise, 
                innovative solutions, and unwavering commitment to excellence.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  sx={{
                    bgcolor: '#1976d2',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    '&:hover': { bgcolor: '#1565c0' }
                  }}
                >
                  Get Started
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    '&:hover': {
                      borderColor: '#1565c0',
                      color: '#1565c0',
                      bgcolor: 'rgba(25, 118, 210, 0.04)'
                    }
                  }}
                >
                  Learn More
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '400px',
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 2,
                  backdropFilter: 'blur(10px)'
                }}
              >
                <Typography variant="h4" color="text.secondary">
                  Technology Visualization
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Services Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3
              }}
            >
              Our Services
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Comprehensive technology solutions designed to drive your business forward
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {services.map((service, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                      {service.icon}
                      <Typography
                        variant="h5"
                        sx={{
                          ml: 2,
                          fontWeight: 'bold',
                          color: '#1a1a1a'
                        }}
                      >
                        {service.title}
                      </Typography>
                    </Box>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {service.description}
                    </Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {service.features.map((feature, idx) => (
                        <Chip
                          key={idx}
                          label={feature}
                          size="small"
                          sx={{
                            bgcolor: '#e3f2fd',
                            color: '#1976d2',
                            fontWeight: 'bold'
                          }}
                        />
                      ))}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Stats Section */}
      <Box sx={{ py: 8, bgcolor: '#1976d2', color: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} sx={{ textAlign: 'center' }}>
            {stats.map((stat, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Typography
                  variant="h2"
                  sx={{
                    fontWeight: 'bold',
                    mb: 1
                  }}
                >
                  {stat.number}
                </Typography>
                <Typography variant="h6">
                  {stat.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Insights Section */}
      <Box sx={{ py: 10, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 'bold',
                color: '#1a1a1a',
                mb: 3
              }}
            >
              Insights & Expertise
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: '#666',
                maxWidth: '600px',
                mx: 'auto'
              }}
            >
              Stay ahead with our latest insights on technology trends and best practices
            </Typography>
          </Box>

          <Grid container spacing={4}>
            {insights.map((insight, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 8px 25px rgba(0,0,0,0.1)'
                    }
                  }}
                >
                  <CardContent sx={{ p: 4 }}>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#1a1a1a',
                        mb: 2
                      }}
                    >
                      {insight.title}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#666',
                        mb: 3,
                        lineHeight: 1.6
                      }}
                    >
                      {insight.description}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Avatar sx={{ bgcolor: '#1976d2', mr: 2, width: 32, height: 32 }}>
                        {insight.author.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {insight.author}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {insight.role}
                        </Typography>
                      </Box>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {insight.readTime}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: 10, bgcolor: '#f8f9fa' }}>
        <Container maxWidth="lg">
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              bgcolor: '#1976d2',
              color: 'white',
              borderRadius: 2
            }}
          >
            <Typography
              variant="h3"
              sx={{
                fontWeight: 'bold',
                mb: 3
              }}
            >
              Ready to Transform Your Business?
            </Typography>
            <Typography
              variant="h5"
              sx={{
                mb: 4,
                opacity: 0.9
              }}
            >
              Let's discuss how we can help you achieve your technology goals
            </Typography>
            <Button
              variant="contained"
              size="large"
              sx={{
                bgcolor: 'white',
                color: '#1976d2',
                px: 6,
                py: 2,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                '&:hover': {
                  bgcolor: '#f5f5f5'
                }
              }}
            >
              Contact Us Today
            </Button>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default HomePage;
